import React from 'react';
import { cn } from '../../lib/utils';
import { InfoIcon } from './Icons';

export type CalloutVariant = 'success' | 'danger' | 'warning' | 'info';

export type CalloutProps = {
  variant?: CalloutVariant;
  title?: React.ReactNode;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  /** Defaults to `status`; use `alert` for blocking/error callouts. */
  role?: 'status' | 'alert';
};

const VARIANT: Record<
  CalloutVariant,
  { shell: string; icon: string; title: string; body: string }
> = {
  success: {
    shell: 'border-green-200/90 bg-green-50/90 dark:border-green-800/50 dark:bg-green-950/30',
    icon: 'text-green-700 dark:text-green-400',
    title: 'text-green-950 dark:text-green-100',
    body: 'text-green-900/90 dark:text-green-200/90',
  },
  danger: {
    shell: 'border-red-200/90 bg-red-50/90 dark:border-red-800/50 dark:bg-red-950/30',
    icon: 'text-red-700 dark:text-red-400',
    title: 'text-red-950 dark:text-red-100',
    body: 'text-red-900/90 dark:text-red-200/90',
  },
  warning: {
    shell: 'border-amber-200/90 bg-amber-50/90 dark:border-amber-800/50 dark:bg-amber-950/30',
    icon: 'text-amber-700 dark:text-amber-400',
    title: 'text-amber-950 dark:text-amber-100',
    body: 'text-amber-900/90 dark:text-amber-200/90',
  },
  info: {
    shell: 'border-sky-200/90 bg-sky-50/90 dark:border-sky-800/50 dark:bg-sky-950/30',
    icon: 'text-sky-700 dark:text-sky-400',
    title: 'text-sky-950 dark:text-sky-100',
    body: 'text-sky-900/90 dark:text-sky-200/90',
  },
};

/**
 * Inline banner for linked-context / blocked-action / tip messages on forms and details.
 */
export function Callout({
  variant = 'info',
  title,
  children,
  icon,
  className,
  role,
}: CalloutProps) {
  const v = VARIANT[variant];
  const resolvedRole = role ?? (variant === 'danger' ? 'alert' : 'status');

  return (
    <div
      role={resolvedRole}
      className={cn(
        'flex flex-col gap-3 rounded-xl border px-4 py-3 sm:px-5 sm:py-3.5',
        v.shell,
        className,
      )}
    >
      <div className="flex min-w-0 gap-3">
        <span className={cn('mt-0.5 shrink-0', v.icon)} aria-hidden>
          {icon ?? <InfoIcon className="h-5 w-5" />}
        </span>
        <div className="min-w-0">
          {title != null && (
            <p className={cn('text-sm font-semibold', v.title)}>{title}</p>
          )}
          {children != null && (
            <div className={cn(title != null && 'mt-0.5', 'text-sm', v.body)}>{children}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Callout;
